import type { PointerEvent as ReactPointerEvent } from 'react';
import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { ArrowLeft, Wrench } from 'lucide-react';

import { IS_DEV } from '@muzly/shared';

/**
 * DevToolLauncher — 개발 빌드 전용, 드래그로 옮길 수 있는 플로팅 진입점.
 *
 * 루트(/)가 온보딩이 되면서 엔진 디버그 콘솔(/debug)로 갈 UI 경로가 사라졌다.
 * dev에서만 뜨는 이 버튼으로 앱↔콘솔을 토글한다. 화면마다 비는 자리가 달라
 * 위치를 드래그로 바꿀 수 있게 하고 localStorage에 기억한다. 프로덕션
 * (IS_DEV=false)에선 렌더하지 않는다.
 */

interface Pos {
  x: number;
  y: number;
}

const STORAGE_KEY = 'muzly.devtool.pos';
const DRAG_THRESHOLD = 6; // 이만큼 움직여야 드래그 — 미만은 탭(내비게이션)
const MARGIN = 8; // 뷰포트 가장자리 여백

// WebView가 edge-to-edge라 뷰포트는 상태바/홈인디케이터 밑까지 뻗는다 → 주입된
// --safe-area-* 만큼 안쪽으로 물려 상태바 침범을 막는다(브라우저에선 값 없어 0).
const readInset = (name: string): number => {
  const v = parseFloat(
    getComputedStyle(document.documentElement).getPropertyValue(name),
  );
  return Number.isFinite(v) ? v : 0;
};

const clamp = (x: number, y: number, el: HTMLElement): Pos => {
  const top = readInset('--safe-area-top');
  const bottom = readInset('--safe-area-bottom');
  const left = readInset('--safe-area-left');
  const right = readInset('--safe-area-right');
  return {
    x: Math.min(
      Math.max(left + MARGIN, x),
      window.innerWidth - el.offsetWidth - right - MARGIN,
    ),
    y: Math.min(
      Math.max(top + MARGIN, y),
      window.innerHeight - el.offsetHeight - bottom - MARGIN,
    ),
  };
};

const loadPos = (): Pos | null => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as Pos) : null;
  } catch {
    return null;
  }
};

export const DevToolLauncher = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const btnRef = useRef<HTMLButtonElement>(null);
  const [pos, setPos] = useState<Pos | null>(null);
  // 드래그 상태(오프셋·시작점·이동여부)와 "직전 드래그였으니 click 무시" 플래그.
  const drag = useRef<{
    ox: number;
    oy: number;
    sx: number;
    sy: number;
  } | null>(null);
  const movedRef = useRef(false);

  useEffect(() => {
    const el = btnRef.current;
    if (!el) {
      return;
    }
    const fallback: Pos = {
      x: window.innerWidth - el.offsetWidth - MARGIN,
      y: window.innerHeight / 2 - el.offsetHeight / 2,
    };
    const initial = loadPos() ?? fallback;
    setPos(clamp(initial.x, initial.y, el));
    const reclamp = () => setPos((p) => (p ? clamp(p.x, p.y, el) : p));
    window.addEventListener('resize', reclamp);
    return () => window.removeEventListener('resize', reclamp);
  }, []);

  if (!IS_DEV) {
    return null;
  }

  const onDebug = pathname.startsWith('/debug');

  const onPointerDown = (e: ReactPointerEvent<HTMLButtonElement>) => {
    const el = btnRef.current;
    if (!el || !pos) {
      return;
    }
    el.setPointerCapture(e.pointerId);
    movedRef.current = false;
    drag.current = {
      ox: e.clientX - pos.x,
      oy: e.clientY - pos.y,
      sx: e.clientX,
      sy: e.clientY,
    };
  };

  const onPointerMove = (e: ReactPointerEvent<HTMLButtonElement>) => {
    const el = btnRef.current;
    const d = drag.current;
    if (!el || !d) {
      return;
    }
    if (
      !movedRef.current &&
      Math.hypot(e.clientX - d.sx, e.clientY - d.sy) < DRAG_THRESHOLD
    ) {
      return;
    }
    movedRef.current = true;
    setPos(clamp(e.clientX - d.ox, e.clientY - d.oy, el));
  };

  const onPointerUp = (e: ReactPointerEvent<HTMLButtonElement>) => {
    btnRef.current?.releasePointerCapture(e.pointerId);
    drag.current = null;
    if (movedRef.current && pos) {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(pos));
      } catch {
        /* localStorage 불가 — 위치 기억만 생략 */
      }
    }
  };

  // 탭이면 내비게이션, 드래그 직후 합성 click은 무시(키보드 Enter는 그대로 동작).
  const onClick = () => {
    if (movedRef.current) {
      movedRef.current = false;
      return;
    }
    navigate(onDebug ? '/' : '/debug');
  };

  return (
    <button
      ref={btnRef}
      type="button"
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onClick={onClick}
      aria-label={onDebug ? '앱으로 돌아가기' : '디버그 콘솔 열기'}
      title={onDebug ? '앱으로 (드래그로 이동)' : '디버그 콘솔 (드래그로 이동)'}
      style={{
        left: pos?.x ?? 0,
        top: pos?.y ?? 0,
        visibility: pos ? 'visible' : 'hidden',
        touchAction: 'none',
      }}
      className="fixed z-[60] flex cursor-grab touch-none select-none items-center gap-1 rounded-lg border border-border bg-background/80 py-2 pl-2 pr-2.5 text-muted-foreground shadow-md backdrop-blur transition-colors hover:text-foreground active:cursor-grabbing"
    >
      {onDebug ? (
        <ArrowLeft className="h-4 w-4" />
      ) : (
        <Wrench className="h-4 w-4" />
      )}
      <span className="text-[10px] font-semibold uppercase tracking-wide">
        {onDebug ? 'APP' : 'DEV'}
      </span>
    </button>
  );
};
